/* eslint-disable no-debugger */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* tslint:disable: no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { AttributesForSelectedVariantInput, getAttributesForSelectedVariant } from '@msdyn365-commerce-modules/product-specification';
import { ILobasProductDocumentAttributeData } from './lobas-product-document-attribute.data';
import { ILobasProductDocumentAttributeProps } from './lobas-product-document-attribute.props.autogenerated';
import { ILobasLinkProps } from '../components/lobas-table';
import {
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick,
    getTelemetryObject
} from '@msdyn365-commerce-modules/utilities';
import { ILobasProductAttributeData } from '../lobas-product-attribute/lobas-product-attribute.data';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

export interface ILobasProductDocumentAttributeViewProps extends ILobasProductDocumentAttributeProps<ILobasProductAttributeData> {
    lobasProductDocumentAttributeContainer?: (JSX.Element | null)[];
    className?: string;
}

/**
 *
 * ProductSpecification component.
 * @extends {React.Component<ILobasDocumentAttributeProps<ILobasProductDocumentAttributeData>>}
 */
@observer
class LobasProductDocumentAttribute extends React.Component<ILobasProductDocumentAttributeProps<ILobasProductDocumentAttributeData>> {
    public async componentDidMount(): Promise<void> {
        const { context, data, telemetry } = this.props;

        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            () => {
                if (context && data.product.result) {
                    const actionInput = new AttributesForSelectedVariantInput(
                        data.product.result.RecordId,

                        +context.actionContext.requestContext.apiSettings.channelId,
                        data.product.result
                    );

                    getAttributesForSelectedVariant(actionInput, this.props.context.actionContext).catch((error: Error) => {
                        telemetry.error(error.message);
                        telemetry.debug('Unable to update attributes for Product');
                        telemetry.error(error.message);
                    });
                }
            }
        );
    }

    public render(): JSX.Element | null {
        let data: ILobasProductDocumentAttributeData;

        try {
            data = this.props.data;
        } catch (error) {
            this.props.telemetry.error(`Something went wrong ------${error}`);
            return <div>{error}</div>;
        }

        if (!data.productSpecificationData.result || data.productSpecificationData.result.length <= 0) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            return null;
        }

        const { className, selectedAttribute } = this.props.config;

        const viewProps = {
            ...this.props,
            lobasProductDocumentAttributeContainer: this._createImageContainer(data, selectedAttribute),
            className: classnames('ms-text-block', className)
        } as ILobasProductDocumentAttributeViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleAttributeChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.selectedAttribute = event.target.value);

    private _createLink(content: AttributeValue): JSX.Element | null {
        const baseUrl = `https://lob.as/dokumentasjon/${this.props.config.documentType}`;

        const link: ILobasLinkProps = {
            linkUrl: `${baseUrl}/${content.TextValue}`,
            linkText: content.Name,
            linkType: '',
            disabled: false
        };

        // Construct telemetry attribute to render
        const payLoad = getPayloadObject('click', getTelemetryContent(this.props), '', '');
        const linkText = link.linkText ? link.linkText : '';
        payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(getTelemetryContent(this.props), payLoad);
        const btnClass = 'lobas-link-btn__primary';

        const editableLink: Msdyn365.ILinksData = {
            ariaLabel: '',
            className: btnClass,
            linkText: link.linkText,
            linkUrl: link.linkUrl,
            openInNewTab: true,
            role: 'link',
            additionalProperties: attributes,
            onClick: onTelemetryClick(getTelemetryContent(this.props), payLoad, linkText)
        };

        return (
            <div className='lobas-document-link' key={content.RecordId}>
                <Msdyn365.Link link={editableLink} />
            </div>
        );
    }

    private _createImageContainer(
        content: ILobasProductDocumentAttributeData,
        selectedAttribute: string | undefined
    ): (JSX.Element | null)[] {
        return content.productSpecificationData
            .result!.filter(name => name.Name === selectedAttribute)
            .map((product, index) => {
                if (product.TextValue) {
                    return (
                        <div className={`lobas-document-type-${this.props.config.documentType}`} key={index}>
                            {this._createLink(product)}
                        </div>
                    );
                } else {
                    return null;
                }
            });
    }
}

export function getTelemetryContent(props: ILobasProductDocumentAttributeProps<ILobasProductDocumentAttributeData>): ITelemetryContent {
    return getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
}

export default LobasProductDocumentAttribute;
